import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, CheckCircle, ChevronLeft, ChevronRight, Clock, RefreshCw, X } from 'react-feather';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  Select,
  Table,
  TextInput,
  Toolbar,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import ClickToCall from '../../../Telephony/ClickToCall';
import {
  currentActor,
  customerLocalTime,
  FactProperty,
  FieldValues,
  fetchTasksMe,
  isTasksLeaderRole,
  isTicketType,
  RM_TASKS_ENABLED,
  summarizeAnswers,
  Task,
  TasksMe,
  tasksApi,
  TaskTypeCfg,
  TicketConfig,
} from '../../../Tasks/tasksApi';
import { CountryFlag, LocalTimeDisplay, TaskIdChip } from './TaskActionModal';
import StudentDrawer, { StudentRef } from './StudentDrawer';
import TicketSubFilters, { matchesSubFilters, SubFilters } from './TicketSubFilters';
import { TaskDetailView } from './TaskDetailPage';
import { Callout, Drawer, IconButton, Segmented } from './ui-pending';
import './TaskQueue.scss';

/** Wide screens show the ticket beside the list; narrower ones as a drawer. */
const useWide = (query = '(min-width: 1200px)'): boolean => {
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = (): void => setWide(m.matches);
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [query]);
  return wide;
};


type Urgency = 'OVERDUE' | 'TODAY' | 'UPCOMING';
/** Queue chips: urgency buckets plus the shared stages (Completed = Closed). */
type ChipKey =
  | ''
  | Urgency
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'RES_OVERDUE'
  | 'FOLLOWUP'
  | 'COMPLETED'
  | 'NO_CLASSES'
  | 'FINISHED_TODAY';
const CHIP_KEYS: ChipKey[] = ['OPEN', 'IN_PROGRESS', 'OVERDUE', 'RES_OVERDUE', 'TODAY', 'UPCOMING', 'FOLLOWUP', 'COMPLETED', 'NO_CLASSES', 'FINISHED_TODAY'];

/** Chips that only exist on the renewal queue, where the figures do. */
const RENEWAL_ONLY_CHIPS: ChipKey[] = ['NO_CLASSES', 'FINISHED_TODAY'];

/**
 * A learner with nothing left on their plan — the renewal queue's first call.
 *
 * Read from the facts the server attaches to each row rather than fetched
 * again, so the chip counts the same way every other chip does. A ticket
 * whose learner the warehouse has not reached yet has no number and is not
 * counted: absent is not zero.
 */
/**
 * Facts drawn beside the learner's name instead of in a column of their own.
 *
 * A membership tier describes who is being rung, not a quantity to sort a
 * queue by, and only 8% of enrolments have one — as a column it would be
 * nine-tenths dashes, while a badge is simply absent when there is nothing
 * to say.
 */
const BADGE_KEYS = ['membershipType'];

/** Highest tier first, so the eye lands on the one worth prioritising. */
const TIER_RANK: Record<string, number> = { PLATINUM: 3, GOLD: 2, SILVER: 1 };

const MembershipBadge = ({ value }: { value: unknown }): JSX.Element | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const tier = value.trim().toUpperCase();
  // An unrecognised tier still shows, in the neutral style: a new one the
  // core system starts sending should appear rather than vanish because we
  // have no colour for it yet.
  const known = tier in TIER_RANK;
  return (
    <span
      className={`tq-tier${known ? ` tq-tier--${tier.toLowerCase()}` : ''}`}
      title={`${value} membership on this course`}
    >
      {tier}
    </span>
  );
};

const noClassesLeft = (t: Task): boolean => Number(t.facts?.classesRemaining) === 0;

/**
 * Finished their plan today: the last class was taken today and there is
 * nothing left after it.
 *
 * The narrowest call list there is, and the warmest — the learner has just
 * walked out of their final class. Today is read in IST, the day the
 * warehouse dates are written in and the one ops reports on, so an RM on the
 * US desk at 2am still sees the same day's list as everyone else.
 */
const istToday = (): string =>
  new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

const finishedToday = (t: Task): boolean =>
  noClassesLeft(t) && String(t.facts?.lastClassTakenDate ?? '').slice(0, 10) === istToday();

/** The server's own per-request maximum. */
const PAGE = 200;
/**
 * A ceiling, so a queue that grows past anything we have seen cannot hang the
 * page. Reaching it puts the "newest N of M" notice back on screen.
 */
const MAX_ROWS = 5000;

/**
 * Every page of a task query, not only the first.
 *
 * The chips, the counts above them, the sub-filter menus and the agent
 * dropdown are all computed from what the browser holds. So a single capped
 * request did not merely hide older rows — it made those numbers wrong, and
 * clearing a filter to see more could return fewer. Page one tells us the
 * total; the rest are fetched together rather than one after another.
 */
const fetchAllTasks = async (
  base: URLSearchParams,
  api: <T>(path: string) => Promise<T>,
): Promise<{ tasks: Task[]; total: number }> => {
  const pageParams = (skip: number): string => {
    const p = new URLSearchParams(base);
    p.set('take', String(PAGE));
    p.set('skip', String(skip));
    return p.toString();
  };
  const head = await api<{ tasks: Task[]; total: number }>(`/v1/tasks?${pageParams(0)}`);
  const want = Math.min(head.total, MAX_ROWS);
  if (head.tasks.length >= want) return head;
  const rest = await Promise.all(
    Array.from({ length: Math.ceil((want - PAGE) / PAGE) }, (_, i) =>
      api<{ tasks: Task[] }>(`/v1/tasks?${pageParams((i + 1) * PAGE)}`).catch(() => ({ tasks: [] as Task[] })),
    ),
  );
  // Rows are ordered newest-first, so a ticket created while we page shifts
  // the window and can repeat a row. Keep the first copy of each id.
  const seen = new Set<string>();
  const tasks = [...head.tasks, ...rest.flatMap((r) => r.tasks)].filter((t) =>
    seen.has(t.id) ? false : (seen.add(t.id), true),
  );
  return { tasks, total: head.total };
};

const URGENCY_PILL: Record<Urgency, { label: string; tone: BadgeTone }> = {
  OVERDUE: { label: 'Overdue', tone: 'danger' },
  TODAY: { label: 'Due today', tone: 'warning' },
  UPCOMING: { label: 'Upcoming', tone: 'primary' },
};

/** Text tone for the SLA column; each maps to a `tq-tone--*` class. */
type TextTone = 'default' | 'muted' | 'success' | 'warning' | 'danger';

/** Closed tickets: outcome → badge tone. */
const resolutionTone = (r: Task['resolution']): BadgeTone =>
  r === 'SUCCESS' ? 'success' : r === 'CANCELLED' ? 'neutral' : 'danger';

/**
 * The SLA is "action it within N hours", so a ticket already worked is not
 * overdue however long it stays open — only untouched ones race the clock.
 */
const urgencyOf = (t: Task): Urgency => {
  if (!t.dueAt || t.firstResponseAt) return 'UPCOMING';
  const due = new Date(t.dueAt);
  const now = new Date();
  if (due.getTime() < now.getTime()) return 'OVERDUE';
  return due.toDateString() === now.toDateString() ? 'TODAY' : 'UPCOMING';
};

/** Created / actioned times are shown in IST — ops' reporting day. */
const IST = 'Asia/Kolkata';
const istDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
const istFull = (iso: string): string =>
  `${new Date(iso).toLocaleString('en-IN', { timeZone: IST, dateStyle: 'full', timeStyle: 'short' })} IST`;

/**
 * "2d 4h" / "21h" / "45m" — how much of a deadline is left, or how far past
 * it we are. Always a duration, never a date: "Tomorrow" told an RM nothing
 * about how long they actually had, which is the only thing they need from
 * this column.
 */
const untilOf = (iso: string): string => {
  const mins = Math.round(Math.abs(new Date(iso).getTime() - Date.now()) / 60_000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  if (d >= 1) return h ? `${d}d ${h}h` : `${d}d`;
  if (h >= 1) return `${h}h ${mins % 60}m`;
  return `${mins}m`;
};

/** The exact deadline, for the tooltip — IST, same as every other time here. */
const deadlineTitle = (label: string, iso: string): string =>
  `${label} ${new Date(iso).toLocaleString('en-IN', { timeZone: IST, dateStyle: 'medium', timeStyle: 'short' })} IST`;

/** "2h 10m" / "35m" — how long something took. */
const spanOf = (fromIso: string, toIso: string): string => {
  const mins = Math.max(0, Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000));
  const h = Math.floor(mins / 60);
  return h > 48 ? `${Math.floor(h / 24)}d` : h ? `${h}h ${mins % 60}m` : `${mins}m`;
};

/**
 * The second SLA on the sheet: "Resolution Time" — how long the ticket may
 * stay open, counted in the RM's working days. Shown under the action clock,
 * and only when it says something the action clock doesn't: a deadline
 * running, or a close that missed it.
 */
const resolutionNote = (t: Task): { text: string; tone: TextTone; bold?: boolean } | null => {
  if (!t.resolveDueAt) return null;
  const due = new Date(t.resolveDueAt);
  if (t.closedAt) {
    return new Date(t.closedAt) > due ? { text: 'Closed past resolution SLA', tone: 'warning' } : null;
  }
  if (due.getTime() < Date.now()) {
    const days = Math.floor((Date.now() - due.getTime()) / 86_400_000);
    return {
      text: days >= 1 ? `Resolution overdue ${days}d` : 'Resolution overdue',
      tone: 'warning',
      bold: true,
    };
  }
  return {
    text: `Resolve in ${untilOf(t.resolveDueAt)}`,
    tone: 'muted',
  };
};

/**
 * Past the resolution deadline and still open.
 *
 * This is a different question from `urgencyOf(t) === 'OVERDUE'`, and the two
 * barely overlap: the action clock stops the moment an RM touches the ticket,
 * so every ticket being worked drops out of it. The resolution clock keeps
 * running until the ticket closes. A ticket actioned inside ten minutes and
 * then left open for three weeks is on time by one SLA and badly late by the
 * other — and it was the count nobody could see.
 */
const resolutionOverdue = (t: Task): boolean =>
  !!t.resolveDueAt && !t.closedAt && new Date(t.resolveDueAt).getTime() < Date.now();

/** One clickable count in the strip above the filter toolbar. */
interface QueueChip {
  key: ChipKey;
  label: string;
  count: number;
  /** Shows the count in the alert colour — something is wrong, not just non-zero. */
  alert?: boolean;
  /** Defaults to the red the action SLA uses; Past resolution is amber, as on
   *  the RM Dashboard, so the two breaches stay visually distinct. */
  alertTone?: 'danger' | 'warning';
  title?: string;
}

/**
 * The SLA cell: once an RM has worked the ticket it shows how long that
 * took (met or missed); until then it counts down to the deadline.
 */
/**
 * Sorting the queue.
 *
 * The list already holds every matching row (it pages client-side), so a sort
 * reorders the whole result rather than shuffling the visible page — which
 * would be worse than no sort at all.
 *
 * Each column sorts by the value it *shows*, with one exception: SLA sorts by
 * the deadline rather than the words in the cell, so "Action overdue 3d" and
 * "Due in 2h" land either side of now instead of next to each other
 * alphabetically.
 */
export // A fact column sorts by its own key, prefixed so it cannot collide with the
// fixed columns above.
type SortKey = 'student' | 'task' | 'agent' | 'created' | 'sla' | 'status' | `fact:${string}`;
export type SortDir = 'asc' | 'desc';

/** Rows with nothing to sort on sit at the end, whichever way the arrow points. */
const LAST = Number.POSITIVE_INFINITY;

const sortValue = (t: Task, key: SortKey): string | number => {
  if (key.startsWith('fact:')) {
    const v = t.facts?.[key.slice(5)];
    // A learner the warehouse has not reached has no number at all, which is
    // not the same as zero — they sort to the end rather than alongside the
    // ones with nothing left.
    if (v === undefined || v === null || v === '') return LAST;
    return typeof v === 'number' ? v : String(v);
  }
  switch (key) {
    case 'student':
      return (t.customerName ?? '').toLowerCase();
    case 'task':
      return (t.taskType?.name ?? '').toLowerCase();
    case 'agent':
      // Unassigned last ascending, which is where someone scanning for an
      // owner expects the gap to be.
      return t.assignee?.name ? t.assignee.name.toLowerCase() : '\uffff';
    case 'created':
      return new Date(t.createdAt).getTime();
    case 'sla':
      // A ticket already actioned has no clock left to run, so it sorts after
      // everything still waiting rather than by a deadline nobody is chasing.
      if (t.firstResponseAt) return LAST;
      return t.dueAt ? new Date(t.dueAt).getTime() : LAST;
    case 'status':
      return (t.status?.label ?? t.stage ?? '').toLowerCase();
    default:
      return '';
  }
};

export const bySort = (key: SortKey, dir: SortDir) => (a: Task, b: Task): number => {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  if (av === bv) return 0;
  // Keep the "no value" rows pinned to the end both ways round.
  if (av === LAST) return 1;
  if (bv === LAST) return -1;
  const order = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
  return dir === 'asc' ? order : -order;
};

/**
 * A column header you can sort by. Carries the state in `aria-sort` as well
 * as the arrow, so the order is announced rather than only drawn.
 */
const SortableTh = ({
  sortKey,
  sort,
  onSort,
  title,
  children,
}: {
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir } | null;
  onSort: (key: SortKey) => void;
  title?: string;
  children: React.ReactNode;
}): JSX.Element => {
  const active = sort?.key === sortKey;
  const hint = active
    ? sort!.dir === 'asc'
      ? 'Sorted A→Z. Click to reverse.'
      : 'Sorted Z→A. Click to clear.'
    : 'Click to sort';
  return (
    <th
      aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={active ? 'tq-th tq-th--sorted' : 'tq-th'}
    >
      <button
        type="button"
        className="tq-th__btn"
        onClick={() => onSort(sortKey)}
        title={title ? `${title}\n\n${hint}` : hint}
      >
        <span>{children}</span>
        <span className="tq-th__arrow" aria-hidden="true">
          {active ? (sort!.dir === 'asc' ? '\u2191' : '\u2193') : '\u2195'}
        </span>
      </button>
    </th>
  );
};

/**
 * One warehouse value in the queue.
 *
 * Blank is a real answer here and must not read as zero: a learner the
 * warehouse has not reached yet has no figure, which is a different thing
 * from having none left. A count of zero is the one worth looking at, so it
 * is the only value that gets any emphasis.
 */
const FactCell = ({
  value,
  kind,
}: {
  value: string | number | boolean | null | undefined;
  kind: string;
}): JSX.Element => {
  if (value === undefined || value === null || value === '') {
    return <span className="ds-text-subtle" title="No figure from the core system yet">—</span>;
  }
  if (kind === 'date') {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? (
      <>{String(value)}</>
    ) : (
      <span title={String(value)}>
        {d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
      </span>
    );
  }
  if (kind === 'number') {
    const n = Number(value);
    return n === 0 ? (
      <span className="tq-fact--none" title="Nothing left on the plan — call these first">0</span>
    ) : (
      <>{n}</>
    );
  }
  return <>{String(value)}</>;
};

const slaCell = (t: Task): { text: string; tone: TextTone; bold: boolean } => {
  if (t.firstResponseAt) {
    const missed = !!t.dueAt && new Date(t.firstResponseAt) > new Date(t.dueAt);
    return {
      text: `Actioned in ${spanOf(t.createdAt, t.firstResponseAt)}`,
      tone: missed ? 'warning' : 'success',
      bold: false,
    };
  }
  if (!t.dueAt) return { text: '—', tone: 'default', bold: false };
  const due = new Date(t.dueAt);
  const diffMs = due.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const hours = Math.floor(abs / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  if (diffMs < 0) {
    const span = hours >= 48 ? `${Math.floor(hours / 24)}d` : `${hours}h`;
    return { text: `Action overdue ${span}`, tone: 'danger', bold: true };
  }
  // Same shape whatever the distance: "Act in <time left>". It used to say
  // "Tomorrow" or "11 Oct", which read as a date with no duration and sat
  // directly above "Resolve by ..." — so the two deadlines looked alike and
  // neither said how long was left.
  const span = untilOf(t.dueAt);
  if (due.toDateString() === new Date().toDateString()) {
    return { text: `Act in ${hours}h ${minutes}m`, tone: 'warning', bold: true };
  }
  return { text: `Act in ${span}`, tone: 'default', bold: true };
};

/**
 * Reason / context line assembled from what the task actually carries. On
 * the unified ticket model: stage (or closing reason) · last activity · the
 * first captured answer, e.g. "In Progress · Connected · Personal › Travelling".
 */
const reasonOf = (t: Task, cfg?: TicketConfig | null): string => {
  const ctx = (t.context ?? {}) as Record<string, unknown>;
  const activity = cfg && t.lastActivityKey
    ? cfg.activities.find((a) => a.key === t.lastActivityKey)?.label
    : undefined;
  const answer = cfg ? summarizeAnswers(cfg, (t.fieldValues ?? {}) as FieldValues)[0]?.value : undefined;
  const parts = [
    t.status.label,
    activity,
    answer,
    ctx.courseName as string | undefined,
    ctx.expiryDate ? `expires ${ctx.expiryDate}` : undefined,
    ctx.lastPlan as string | undefined,
  ].filter(Boolean);
  return parts.join(' · ');
};

interface TaskQueueProps {
  /**
   * Which types this queue covers:
   *   'all'          — the Tasks menu: every type, renewals included, so an
   *                    RM works one list instead of switching menus;
   *   'renewal-only' — the Renewals menu, for when that is all you want;
   *   'non-renewal'  — the older Tasks behaviour, kept for anything that
   *                    still wants renewals held back.
   */
  scope: 'all' | 'renewal-only' | 'non-renewal';
  title: string;
  subtitle: string;
}

/** Local calendar date as YYYY-MM-DD (the wire format for the range filter). */
const isoDay = (d: Date): string => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

/** Parse a YYYY-MM-DD string to a local Date (midnight), or null if empty. */
const parseDay = (s: string): Date | null => (s ? new Date(`${s}T00:00:00`) : null);

/**
 * The urgency-first work queue shared by Renewals and Tasks — layout follows
 * Kartheek's "My Tasks" mockup: stat cards, chip filters, SLA column. SLA
 * adherence and Best call window cards are sample data pending core APIs.
 */
/**
 * The ticket someone means by what they pasted.
 *
 * Rarely the clean form in practice: a bare number read off a Slack message,
 * the reference with its prefix, or the whole URL copied out of the address
 * bar. They all mean the same ticket and the API resolves every form, so the
 * only job here is to find the identifier inside what was pasted. Null when
 * there is nothing ticket-shaped in it, so the box can decline rather than
 * navigate to a page that can only 404.
 */
export function ticketTarget(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  // A pasted link or path: the ticket is its last segment, minus any query.
  const path = text.split(/[?#]/)[0];
  const candidate = path.split('/').filter(Boolean).pop() ?? text;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate)) {
    return candidate;
  }
  const serial = /^(?:RMT[-\s]?)?(\d+)$/i.exec(candidate);
  // Normalised to the display form, so the address bar reads as the reference
  // people actually quote rather than as a bare number.
  return serial ? `RMT-${serial[1]}` : null;
}

const TaskQueue: React.FC<TaskQueueProps> = ({ scope, title, subtitle }) => {
  const me = useMemo(currentActor, []);
  const [allTypes, setAllTypes] = useState<TaskTypeCfg[]>([]);
  const types = useMemo(
    () =>
      allTypes.filter((t) =>
        scope === 'renewal-only'
          ? t.key === 'renewal'
          : scope === 'all' || t.key !== 'renewal',
      ),
    [allTypes, scope],
  );

  const [tasks, setTasks] = useState<Task[]>([]);
  const [closedTasks, setClosedTasks] = useState<Task[]>([]);
  /** >0 when the closed list was capped: how many there really are. */
  const [closedTruncated, setClosedTruncated] = useState(0);
  /** >0 when the open list was capped: how many there really are. */
  const [openTruncated, setOpenTruncated] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters live in the URL query string so a round trip to the task detail
  // page (Act → Back) restores the exact queue the agent left, instead of
  // silently resetting to the defaults on remount. `replace: true` on every
  // write keeps filter tweaks from piling up browser-history entries — Back
  // from the detail page should land on the list once, not step through each
  // filter change.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // What someone typed into "Go to ticket". Not a filter and not in the URL:
  // it is a jump, so it should leave nothing behind once it has been used.
  const [goTo, setGoTo] = useState('');
  const [goToBad, setGoToBad] = useState(false);
  // A learner search, server-side so it reaches past the loaded page. Lives in
  // the URL with the other filters, so a found ticket survives a round trip to
  // the detail page and back.
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [urgencyFilter, setUrgencyFilter] = useState<ChipKey>(() => {
    // Deep links (e.g. the dashboard's attention banner) preselect a chip.
    const wanted = searchParams.get('urgency')?.toUpperCase();
    if (!(CHIP_KEYS as string[]).includes(wanted ?? '')) return '';
    // These exist only on the renewal queue. A link carrying one elsewhere
    // would filter everything away with no chip on screen to clear, so it is
    // ignored rather than honoured into a dead end.
    if (RENEWAL_ONLY_CHIPS.includes(wanted as ChipKey) && scope !== 'renewal-only') return '';
    return wanted as ChipKey;
  });
  // The ticket open beside the list (URL `task`), so Back/refresh keep it.
  const [selectedTaskId, setSelectedTaskId] = useState(() => searchParams.get('task') ?? '');
  const wide = useWide();
  const panelRef = useRef<HTMLDivElement>(null);
  // Jira-style sub-filters for a type on the unified ticket model (URL `sf`).
  const [subFilters, setSubFilters] = useState<SubFilters>(() => {
    try {
      const parsed = JSON.parse(searchParams.get('sf') || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as SubFilters) : {};
    } catch {
      return {};
    }
  });
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get('type') ?? '');
  // Priority is set by the task type, never chosen per ticket, so filtering
  // on it only ever answered "show me the types that happen to be URGENT".
  const [podFilter, setPodFilter] = useState(() => searchParams.get('pod') ?? '');
  const [countryFilter, setCountryFilter] = useState(() => searchParams.get('country') ?? '');
  /** Desks and the countries that have learners, for the two filters. */
  const [pods, setPods] = useState<{ key: string; name: string }[]>([]);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
  const [agentFilter, setAgentFilter] = useState(() => searchParams.get('agent') ?? '');
  // Server-side date-range filter. `dateField` chooses which timestamp the
  // range applies to; a Closed-date range only constrains the Completed list
  // (open tasks have no closedAt), so the working queue is left intact there.
  const [dateField, setDateField] = useState<'created' | 'due' | 'closed'>(() => {
    const f = searchParams.get('dateField');
    return f === 'due' || f === 'closed' ? f : 'created';
  });
  // Default to the last 7 days (matches the "Last 7 days" quick range, so that
  // preset shows as selected on open) — a sensible starting window.
  const [dateFrom, setDateFrom] = useState(() => {
    const fromUrl = searchParams.get('from');
    if (fromUrl) return fromUrl;
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return isoDay(d);
  });
  const [dateTo, setDateTo] = useState(() => searchParams.get('to') ?? isoDay(new Date()));
  // Rows-per-page — persisted alongside the filters so it doesn't quietly
  // reset to 25 on every trip back from a task's detail page.
  const [pageSize, setPageSize] = useState(() => {
    const fromUrl = Number(searchParams.get('pageSize'));
    return [25, 50, 100].includes(fromUrl) ? fromUrl : 25;
  });

  useEffect(() => {
    const next = new URLSearchParams();
    if (urgencyFilter) next.set('urgency', urgencyFilter);
    if (typeFilter) next.set('type', typeFilter);
    if (podFilter) next.set('pod', podFilter);
    if (countryFilter) next.set('country', countryFilter);
    if (agentFilter) next.set('agent', agentFilter);
    if (dateField !== 'created') next.set('dateField', dateField);
    if (dateFrom) next.set('from', dateFrom);
    if (dateTo) next.set('to', dateTo);
    if (pageSize !== 25) next.set('pageSize', String(pageSize));
    if (Object.keys(subFilters).length) next.set('sf', JSON.stringify(subFilters));
    if (search) next.set('q', search);
    if (selectedTaskId) next.set('task', selectedTaskId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urgencyFilter, typeFilter, podFilter, countryFilter, agentFilter, dateField, dateFrom, dateTo, pageSize, subFilters, selectedTaskId, search]);

  const [tasksMe, setTasksMe] = useState<TasksMe | null>(null);
  const isLeader = isTasksLeaderRole(tasksMe?.role);
  const [page, setPage] = useState(0);
  // No sort until someone asks for one: the server's newest-first order is
  // what the queue is meant to be read in, and a default sort would quietly
  // replace it.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);

  /** First click sorts, second reverses, third clears back to newest-first. */
  const toggleSort = (key: SortKey): void => {
    setSort((cur) => {
      if (cur?.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };
  // The student whose detail drawer is open (null = closed).
  const [drawerStudent, setDrawerStudent] = useState<StudentRef | null>(null);
  const [sla, setSla] = useState<{
    current: { met: number; total: number; pct: number | null };
    previous: { met: number; total: number; pct: number | null };
  } | null>(null);

  // `silent` refreshes the rows without the spinner — used after a change
  // in the side panel, so the list doesn't blank out under the user.
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      // Apply the date range to a query. `forList` lets us skip a Closed-date
      // range on the open queue (open tasks have no closedAt → would be empty).
      const applyRange = (p: URLSearchParams, forList: 'open' | 'closed') => {
        if (!dateFrom && !dateTo) return;
        if (dateField === 'closed' && forList === 'open') return;
        p.set('dateField', dateField);
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo) p.set('to', dateTo);
        // The picked days are the agent's own calendar days — the same zone
        // every date on screen is shown in (the service defaults to IST).
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) p.set('tz', tz);
      };
      const params = new URLSearchParams({ open: 'true' });
      // A chosen type is fetched server-side too, so the fetch stays small
      // when one is picked.
      const typeKey = scope === 'renewal-only' ? 'renewal' : typeFilter;
      if (typeKey) params.set('taskTypeKey', typeKey);
      // Desk and country are answered by the service: country comes from the
      // learner's timezone, which the browser does not have, and it is the
      // only thing that separates US from Canada.
      if (podFilter) params.set('podKey', podFilter);
      if (countryFilter) params.set('country', countryFilter);
      if (search) params.set('q', search);
      applyRange(params, 'open');
      const mine = await fetchTasksMe();
      setTasksMe(mine);
      const leader = isTasksLeaderRole(mine?.role);
      const myExternalId = me.externalId;
      // Narrow to one RM server-side whenever we know which one. Every page
      // is fetched now, so this is no longer what makes the list complete —
      // it just keeps the fetch to a single request for the common case.
      const onlyFor = leader ? agentFilter : myExternalId;
      if (onlyFor) params.set('assigneeExternalId', onlyFor);
      const res = await fetchAllTasks(params, tasksApi);
      // Unassigned tickets are nobody's but still claimable, so an agent
      // scoped to themselves has to be shown them too — a second, small call
      // rather than pulling the whole queue back to find them.
      let claimable: Task[] = [];
      if (onlyFor && !leader) {
        const up = new URLSearchParams(params);
        up.delete('assigneeExternalId');
        up.set('unassigned', 'true');
        claimable = await fetchAllTasks(up, tasksApi)
          .then((r) => r.tasks)
          .catch(() => []);
      }
      // Agents see their workspace — their tasks plus unassigned ones (still
      // claimable via Assign to me). Leaders see the whole team's queue.
      const inScope = (t: Task): boolean =>
        (scope === 'renewal-only'
          ? t.taskType.key === 'renewal'
          : scope === 'all' || t.taskType.key !== 'renewal') &&
        (leader || !t.assignee || t.assignee.externalId === myExternalId);
      setTasks([...res.tasks, ...claimable].filter(inScope));
      setOpenTruncated(res.total > res.tasks.length ? res.total : 0);
      const closedParams = new URLSearchParams({ open: 'false' });
      if (search) closedParams.set('q', search);
      if (typeKey) closedParams.set('taskTypeKey', typeKey);
      if (podFilter) closedParams.set('podKey', podFilter);
      if (countryFilter) closedParams.set('country', countryFilter);
      // Narrow the closed list to one RM server-side whenever we can. Closed
      // tickets are the ones that pile up — 1,791 against 396 open — so
      // scoping keeps this to one request instead of nine.
      //
      // Only for the closed list: the open one must keep unassigned tickets
      // visible so an agent can still claim them.
      const closedFor = onlyFor;
      if (closedFor) closedParams.set('assigneeExternalId', closedFor);
      applyRange(closedParams, 'closed');
      fetchAllTasks(closedParams, tasksApi)
        .then((closed) => {
          setClosedTasks(closed.tasks.filter(inScope));
          // Say so when the cap bit, rather than showing a short list as if
          // it were the whole thing.
          setClosedTruncated(closed.total > closed.tasks.length ? closed.total : 0);
        })
        .catch(() => {
          setClosedTasks([]);
          setClosedTruncated(0);
        });
    } catch (err: any) {
      toast.error(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [scope, me.externalId, dateField, dateFrom, dateTo, typeFilter, podFilter, countryFilter, agentFilter, search]);

  // One call gives both lists: the desks, and every country that actually
  // has learners — so the dropdown holds real options rather than all 249.
  useEffect(() => {
    tasksApi<{ pods: { key: string; name: string; countries: { countryCode: string; name: string; learners: number }[] }[] }>(
      '/v1/pods',
    )
      .then((d) => {
        setPods((d.pods ?? []).map((x) => ({ key: x.key, name: x.name })));
        const seen = new Map<string, { code: string; name: string; learners: number }>();
        for (const pod of d.pods ?? []) {
          for (const c of pod.countries ?? []) {
            const prev = seen.get(c.countryCode);
            // A country sits in more than one desk on purpose; count it once.
            if (!prev || c.learners > prev.learners) {
              seen.set(c.countryCode, { code: c.countryCode, name: c.name, learners: c.learners });
            }
          }
        }
        setCountries(
          Array.from(seen.values())
            .filter((c) => c.learners > 0)
            .sort((a, b) => b.learners - a.learners || a.name.localeCompare(b.name))
            .map(({ code, name }) => ({ code, name })),
        );
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    tasksApi<TaskTypeCfg[]>('/v1/task-types')
      .then(setAllTypes)
      .catch((err) => toast.error(`Task service unreachable: ${err.message}`));
  }, []);

  /**
   * Which warehouse properties get a column, and what to call them.
   *
   * Ops own this: a property is shown by marking it visible in the registry,
   * so adding a column is a flag rather than a release. Failing quietly is
   * deliberate — the queue is readable without these, and a registry that is
   * briefly unreachable should cost columns, not the page.
   */
  const [factProps, setFactProps] = useState<FactProperty[]>([]);
  useEffect(() => {
    tasksApi<FactProperty[]>('/v1/fact-properties?visible=true')
      .then((rows) => setFactProps(rows.filter((r) => r.scope === 'ENROLLMENT')))
      .catch(() => setFactProps([]));
  }, []);

  /**
   * Columns stay on the renewal queue: they are figures about a plan, and
   * elsewhere most tickets carry no enrolment at all, so they would be a
   * column of dashes costing width to say nothing.
   *
   * The badge travels everywhere. It says something about the person on the
   * other end of the call, which is worth knowing on any ticket.
   */
  const factCols = useMemo(
    () => (scope === 'renewal-only' ? factProps.filter((p) => !BADGE_KEYS.includes(p.key)) : []),
    [factProps, scope],
  );

  // SLA tile tracks the same type/agent scope as the rest of the dashboard —
  // its own call since it doesn't need a full queue refetch to update.
  useEffect(() => {
    const params = new URLSearchParams();
    if (scope === 'renewal-only') params.set('taskTypeKey', 'renewal');
    else if (typeFilter) params.set('taskTypeKey', typeFilter);
    // 'all' deliberately sets neither: the tile should cover the same work
    // the list shows, renewals included.
    else if (scope !== 'all') params.set('excludeTaskTypeKey', 'renewal');
    if (agentFilter) params.set('assigneeExternalId', agentFilter);
    tasksApi<{
      current: { met: number; total: number; pct: number | null };
      previous: { met: number; total: number; pct: number | null };
    }>(`/v1/tasks/stats/sla?${params.toString()}`)
      .then(setSla)
      .catch(() => setSla(null));
  }, [scope, typeFilter, agentFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-reload when the notifier detects a new task, so the queue stays current
  // without a manual refresh.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener('rm-tasks:changed', onChanged);
    return () => window.removeEventListener('rm-tasks:changed', onChanged);
  }, [load]);

  const ticketCfgByType = useMemo(() => {
    const map: Record<string, TicketConfig> = {};
    allTypes.forEach((t) => {
      if (isTicketType(t)) map[t.key] = t.ticketConfig;
    });
    return map;
  }, [allTypes]);
  // The Renewals page is always renewal, so its sub-filters apply without a type pick.
  const effectiveType = scope === 'renewal-only' ? 'renewal' : typeFilter;

  /**
   * Types worth offering in the filter: the ones that actually hold tickets.
   * The catalog is seeded ahead of the flows that fill it, so most of it sits
   * at zero and a full list is mostly dead options. A type appears on its own
   * the moment its first ticket lands — nothing to switch on.
   *
   * The current pick always stays listed, even at zero, so a filter restored
   * from the URL doesn't quietly disappear from the control showing it.
   */
  const filterTypes = useMemo(() => {
    // A tasks service older than taskCount sends none at all, and filtering
    // on a field that isn't there would empty the dropdown completely —
    // worse than the clutter it set out to fix. Only filter once the counts
    // are actually present, so the two sides can deploy in either order.
    if (!types.some((t) => t.taskCount !== undefined)) return types;
    return types.filter((t) => (t.taskCount ?? 0) > 0 || t.key === typeFilter);
  }, [types, typeFilter]);
  const selectedTicketCfg: TicketConfig | null = effectiveType ? ticketCfgByType[effectiveType] ?? null : null;

  // Type/priority/agent (and a ticket type's sub-filters) narrow every tile
  // and chip below — only the urgency/stage buckets stay independent, since
  // that's what they each count.
  const baseMatch = useCallback(
    (t: Task) =>
      (!typeFilter || t.taskType.key === typeFilter) &&
      (!agentFilter || t.assignee?.externalId === agentFilter),
    [typeFilter, agentFilter],
  );
  const scopeMatch = useCallback(
    (t: Task) => baseMatch(t) && (!selectedTicketCfg || matchesSubFilters(t, subFilters)),
    [baseMatch, selectedTicketCfg, subFilters],
  );
  /**
   * The stage/urgency chip as a predicate, so the list and the sub-filter
   * menus can share it. COMPLETED picks a different source list rather than
   * narrowing this one, so it passes everything here.
   */
  const chipMatch = useCallback(
    (t: Task) =>
      !urgencyFilter ||
      urgencyFilter === 'COMPLETED' ||
      (urgencyFilter === 'FINISHED_TODAY'
        ? finishedToday(t)
        : urgencyFilter === 'NO_CLASSES'
        ? noClassesLeft(t)
        : urgencyFilter === 'FOLLOWUP'
        ? !!t.followUpAt
        : urgencyFilter === 'RES_OVERDUE'
          ? resolutionOverdue(t)
          : urgencyFilter === 'OPEN' || urgencyFilter === 'IN_PROGRESS'
            ? t.stage === urgencyFilter
            : urgencyOf(t) === urgencyFilter),
    [urgencyFilter],
  );
  /**
   * What the sub-filter menus count against: the same tickets the list is
   * showing, before the sub-filters themselves.
   *
   * It used to be every open *and* closed ticket in the type/agent scope,
   * ignoring the chip — so "In Progress 10" sat above options adding up to
   * 19, and the numbers looked broken. They weren't counting the wrong
   * thing so much as counting a different thing than the header.
   */
  const subFilterBase = useMemo(
    () =>
      // Exactly the source `filtered` draws from, including "All" meaning
      // open *and* closed. It used to read open-only for every chip but
      // COMPLETED, which was right until "All" started including closed
      // tickets: a reason held only by a closed ticket then counted 0 while
      // the list below it showed that very ticket. The two must agree, so
      // this mirrors the ternary rather than approximating it.
      (urgencyFilter === 'COMPLETED'
        ? closedTasks
        : urgencyFilter === ''
          ? [...tasks, ...closedTasks]
          : tasks
      ).filter((t) => baseMatch(t) && chipMatch(t)),
    [tasks, closedTasks, urgencyFilter, baseMatch, chipMatch],
  );
  const scopedTasks = useMemo(() => tasks.filter(scopeMatch), [tasks, scopeMatch]);
  const scopedClosedTasks = useMemo(
    () => closedTasks.filter(scopeMatch),
    [closedTasks, scopeMatch],
  );

  // ── Derived stats (current type/priority/agent scope, before urgency) ──
  const overdueCount = scopedTasks.filter((t) => urgencyOf(t) === 'OVERDUE').length;
  const todayTasks = scopedTasks.filter((t) => urgencyOf(t) === 'TODAY');
  const resOverdueCount = scopedTasks.filter(resolutionOverdue).length;

  // Assignees present across this scope's tasks, for the leader's agent filter.
  const agents = useMemo(() => {
    const seen = new Map<string, string>();
    [...tasks, ...closedTasks].forEach((t) => {
      if (t.assignee) seen.set(t.assignee.externalId, t.assignee.name);
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tasks, closedTasks]);

  const viewingCompleted = urgencyFilter === 'COMPLETED';
  // "All" means all: open, in progress and closed. The three stages each
  // have their own chip, so a bucket that silently left closed out was the
  // odd one, and the label had to say "All open" to stay honest.
  const filtered = (
    viewingCompleted
      ? scopedClosedTasks
      : urgencyFilter === ''
        ? [...scopedTasks, ...scopedClosedTasks]
        : scopedTasks
  ).filter(chipMatch);

  // Filters and page-size changes restart from page one.
  useEffect(() => {
    setPage(0);
  }, [urgencyFilter, typeFilter, podFilter, countryFilter, agentFilter, pageSize, dateField, dateFrom, dateTo, subFilters, sort]);

  // Sorted before paging, so page 2 holds the next rows in the chosen order
  // rather than the next rows of the server's order sorted among themselves.
  const ordered = sort ? [...filtered].sort(bySort(sort.key, sort.dir)) : filtered;

  const pageCount = Math.max(1, Math.ceil(ordered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pagedTasks = ordered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  // One clickable summary strip: where tickets stand (stage), then what
  // needs attention (urgency) — replaces the old read-only stat cards.
  const stageChips: QueueChip[] = [
    { key: '', label: 'All', count: scopedTasks.length + scopedClosedTasks.length },
    { key: 'OPEN', label: 'Open', count: scopedTasks.filter((t) => t.stage === 'OPEN').length },
    { key: 'IN_PROGRESS', label: 'In Progress', count: scopedTasks.filter((t) => t.stage === 'IN_PROGRESS').length },
    { key: 'COMPLETED', label: 'Closed', count: scopedClosedTasks.length },
  ];
  // Two of these are SLA breaches and they count different things, so both
  // carry a tooltip saying which clock they mean. "Past resolution" is the
  // name and the amber the RM Dashboard already uses for the same number.
  const focusChips: QueueChip[] = [
    // First, and only here: on the renewal queue the learners with nothing
    // left are the first calls of the day, so the chip leads rather than
    // trailing the SLA buckets.
    ...(scope === 'renewal-only' && scopedTasks.some((t) => t.facts?.classesRemaining !== undefined)
      ? [
          {
            key: 'NO_CLASSES' as ChipKey,
            label: 'No classes left',
            count: scopedTasks.filter(noClassesLeft).length,
            alert: scopedTasks.filter(noClassesLeft).length > 0,
            alertTone: 'warning' as const,
            title:
              'The learner has nothing left on their plan — the first calls to make. Counted from the warehouse figure, so a learner we have no figure for is not counted here.',
          },
          {
            key: 'FINISHED_TODAY' as ChipKey,
            label: 'Finished today',
            count: scopedTasks.filter(finishedToday).length,
            alert: scopedTasks.filter(finishedToday).length > 0,
            alertTone: 'warning' as const,
            title:
              'Took their last class today and have nothing left after it — the warmest call on the list. A subset of "No classes left". Today is the IST day, so the whole team works from the same list.',
          },
        ]
      : []),
    {
      key: 'OVERDUE',
      label: 'Action overdue',
      count: overdueCount,
      alert: overdueCount > 0,
      title: 'Nobody has touched these and the first-action deadline has passed. A ticket the RM has already actioned is never action-overdue, however long it stays open.',
    },
    {
      key: 'RES_OVERDUE',
      label: 'Past resolution',
      count: resOverdueCount,
      alert: resOverdueCount > 0,
      alertTone: 'warning',
      title: 'Still open past the resolution deadline — the ticket should have been closed by now. Unlike Action overdue this keeps counting after the RM has worked the ticket, so it catches the ones that were picked up quickly and then went quiet.',
    },
    { key: 'TODAY', label: 'Due today', count: todayTasks.length, title: 'The first-action deadline falls today.' },
    { key: 'UPCOMING', label: 'Upcoming', count: scopedTasks.length - overdueCount - todayTasks.length },
    { key: 'FOLLOWUP', label: 'Follow-ups', count: scopedTasks.filter((t) => !!t.followUpAt).length },
  ];
  const slaPct = sla?.current.pct ?? null;
  const slaPrev = sla?.previous.pct ?? null;
  const slaDelta = slaPct != null && slaPrev != null ? Math.round((slaPct - slaPrev) * 10) / 10 : null;
  const panelOpen = !!selectedTaskId;
  const panelBeside = panelOpen && wide;

  /**
   * Opening a ticket from far down the list: the panel is pinned beside the
   * list, but the theme's CSS can defeat that, leaving it above the fold.
   * Scroll to it only when it isn't already in view — the reading position
   * in the list stays put either way.
   */
  useEffect(() => {
    if (!selectedTaskId || !panelBeside) return;
    const el = panelRef.current;
    if (!el) return;
    const HEADER = 88;
    const top = el.getBoundingClientRect().top;
    const offScreen = top < HEADER - 8 || top > window.innerHeight * 0.6;
    if (!offScreen) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: window.scrollY + top - HEADER, behavior: reduce ? 'auto' : 'smooth' });
  }, [selectedTaskId, panelBeside]);

  const header = (actions?: React.ReactNode): JSX.Element => (
    <PageHeader eyebrow="RM Tasks" title={title} subtitle={subtitle} actions={actions} />
  );

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        {header()}
        <EmptyState title="Not enabled here" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }

  const slaTone: BadgeTone = slaPct == null ? 'neutral' : slaPct < 80 ? 'danger' : 'success';

  /** Shows the quick-range preset matching the dates, or "Custom" when none does. */
  const quickRange = (() => {
    const today = isoDay(new Date());
    if (dateTo !== today) return '';
    const match = [1, 7, 15, 30].find((n) => {
      const d = new Date();
      d.setDate(d.getDate() - (n - 1));
      return dateFrom === isoDay(d);
    });
    return match ? String(match) : '';
  })();

  /** A chip's label with its count; alert counts take the breach colour. */
  const chipOption = (chip: QueueChip) => ({
    value: chip.key,
    title: chip.title,
    label: (
      <>
        {chip.label}
        <span className={chip.alert ? `tq-chip-count tq-tone--${chip.alertTone ?? 'danger'}` : 'tq-chip-count'}>
          {chip.count}
        </span>
      </>
    ),
  });

  const detailView = (
    <TaskDetailView
      taskId={selectedTaskId}
      variant="panel"
      onChanged={() => load({ silent: true })}
      onClose={() => setSelectedTaskId('')}
    />
  );

  return (
    <Page>
      {header(
        <>
          <span title="Tickets an RM actioned within their SLA — deadlines that fell in the last 30 days (arrow = change vs the 30 days before). Deadlines count the RM's working hours only.">
            <Badge tone={slaTone}>
              SLA {slaPct == null ? '—' : `${slaPct}%`}
              {sla && slaPct != null && (
                <>
                  {' '}
                  · {sla.current.met}/{sla.current.total} on time
                  {slaDelta != null && ` · ${slaDelta >= 0 ? '↑' : '↓'}${Math.abs(slaDelta)}%`}
                </>
              )}
            </Badge>
          </span>
          {/* Straight to a ticket by its reference. The queue's filters answer
              "what needs doing"; this answers "show me that one", which is the
              question a lead arrives with when someone quotes a number. */}
          <form
            className="tq-goto"
            onSubmit={(e) => {
              e.preventDefault();
              // A reference means one ticket, so go straight to it. Anything
              // else is a person — search the queue rather than refuse, which
              // is what this box used to do with a learner's name.
              const typed = goTo.trim();
              if (!typed) return;
              const target = ticketTarget(typed);
              if (target) {
                setGoTo('');
                setGoToBad(false);
                navigate(`${process.env.PUBLIC_URL}/pages/rm-tasks/${target}`);
                return;
              }
              setGoToBad(false);
              setSearch(typed);
            }}
          >
            <SearchInput
              size="sm"
              value={goTo}
              onChange={(e) => {
                setGoTo(e.target.value);
                if (goToBad) setGoToBad(false);
              }}
              placeholder="Ticket, name or number"
              aria-label="Go to a ticket, or search by learner name or number"
              aria-invalid={goToBad || undefined}
              title="RMT-4596 opens that ticket. A learner's name or number searches the queue."
            />
          </form>
          {/* A filtered queue has to say so. Without this the list simply
              shows fewer rows than the counts above it, and the only clue is
              text still sitting in a box. */}
          {search && (
            <Badge tone="info">
              Showing “{search}”
              <button
                type="button"
                className="tq-goto__clear"
                aria-label={`Stop searching for ${search}`}
                title="Clear the search"
                onClick={() => {
                  setSearch('');
                  setGoTo('');
                }}
              >
                <X size={12} aria-hidden />
              </button>
            </Badge>
          )}
          {/* Reloads the whole list — lives with the page, not the filters. */}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => load()}
            title="Refresh the list"
            aria-label="Refresh the list"
            icon={<RefreshCw size={14} aria-hidden />}
          />
        </>,
      )}

      {/* Queue — the list takes the page; the selected ticket opens beside it */}
      <div className="tq-layout">
        <div className="tq-main">
          <Card>
            <div className="ds-stack">
              {/* Filters: clickable summary strip on top, a grouped filter toolbar below */}
              <div className="tq-chips">
                <Segmented
                  size="sm"
                  ariaLabel="Stage"
                  options={stageChips.map(chipOption)}
                  value={urgencyFilter}
                  onChange={setUrgencyFilter}
                />
                <span className="tq-chips__divider" aria-hidden="true" />
                <Segmented
                  size="sm"
                  ariaLabel="Needs attention"
                  options={focusChips.map(chipOption)}
                  value={urgencyFilter}
                  onChange={setUrgencyFilter}
                />
              </div>

              {/* Filter toolbar: who/what first, then dates; a ticket type's own
                  filters get a row of their own so adding one never breaks the first. */}
              <div className="tq-filters">
                <Toolbar>
                  {/* The Renewals page is one type by definition, so it needs
                      no type picker; every other scope does. */}
                  {scope !== 'renewal-only' && (
                    <Field label="Type">
                      {(control) => (
                        <Select
                          {...control}
                          size="sm"
                          value={typeFilter}
                          onChange={(e) => {
                            setTypeFilter(e.target.value);
                            // Sub-filters belong to one type's fields.
                            setSubFilters({});
                          }}
                          options={[
                            { value: '', label: 'All types' },
                            ...filterTypes.map((t) => ({ value: t.key, label: t.name })),
                          ]}
                        />
                      )}
                    </Field>
                  )}
                  {isLeader && (
                    <Field label="Agent">
                      {(control) => (
                        <Select
                          {...control}
                          size="sm"
                          value={agentFilter}
                          onChange={(e) => setAgentFilter(e.target.value)}
                          options={[
                            { value: '', label: 'All agents' },
                            ...agents.map(([externalId, name]) => ({ value: externalId, label: name })),
                          ]}
                        />
                      )}
                    </Field>
                  )}
                  <Field label="Desk">
                    {(control) => (
                      <Select
                        {...control}
                        size="sm"
                        value={podFilter}
                        onChange={(e) => setPodFilter(e.target.value)}
                        options={[{ value: '', label: 'All desks' }, ...pods.map((p) => ({ value: p.key, label: p.name }))]}
                      />
                    )}
                  </Field>
                  <Field label="Country">
                    {(control) => (
                      <Select
                        {...control}
                        size="sm"
                        value={countryFilter}
                        onChange={(e) => setCountryFilter(e.target.value)}
                        options={[
                          { value: '', label: 'All countries' },
                          ...countries.map((c) => ({ value: c.code, label: c.name })),
                        ]}
                      />
                    )}
                  </Field>
                  {/* Quick ranges — set From/To to the last N days ending today.
                      Shows "Custom" when the dates don't match a preset. */}
                  <Field label="Quick range">
                    {(control) => (
                      <Select
                        {...control}
                        size="sm"
                        value={quickRange}
                        onChange={(e) => {
                          const days = Number(e.target.value);
                          if (!days) return;
                          const fromD = new Date();
                          fromD.setDate(fromD.getDate() - (days - 1));
                          setDateFrom(isoDay(fromD));
                          setDateTo(isoDay(new Date()));
                        }}
                        options={[
                          { value: '', label: 'Custom range…' },
                          { value: '1', label: 'Today' },
                          { value: '7', label: 'Last 7 days' },
                          { value: '15', label: 'Last 15 days' },
                          { value: '30', label: 'Last 30 days' },
                        ]}
                      />
                    )}
                  </Field>

                  {/* Date range — which date it filters on, then From – To */}
                  <Field label="Date range" className="tq-dates">
                    {(control) => (
                      <div className="tq-dates__row">
                        <Calendar size={14} className="tq-dates__icon" aria-hidden />
                        <Select
                          {...control}
                          size="sm"
                          className="tq-dates__field"
                          value={dateField}
                          onChange={(e) => setDateField(e.target.value as 'created' | 'due' | 'closed')}
                          title="Which date the range filters on"
                          options={[
                            { value: 'created', label: 'Created' },
                            { value: 'due', label: 'Due' },
                            { value: 'closed', label: 'Closed' },
                          ]}
                        />
                        <DatePicker
                          selected={parseDay(dateFrom)}
                          onChange={(d: Date | null) => setDateFrom(d ? isoDay(d) : '')}
                          maxDate={parseDay(dateTo) ?? undefined}
                          dateFormat="dd-MMM-yyyy"
                          placeholderText="From"
                          customInput={<TextInput size="sm" className="tq-dates__input" title="From date" aria-label="From date" />}
                        />
                        <span className="ds-text-subtle" aria-hidden="true">–</span>
                        <DatePicker
                          selected={parseDay(dateTo)}
                          onChange={(d: Date | null) => setDateTo(d ? isoDay(d) : '')}
                          minDate={parseDay(dateFrom) ?? undefined}
                          dateFormat="dd-MMM-yyyy"
                          placeholderText="To"
                          customInput={<TextInput size="sm" className="tq-dates__input" title="To date" aria-label="To date" />}
                        />
                        {(dateFrom || dateTo) && (
                          <IconButton
                            aria-label="Clear date range"
                            title="Clear date range"
                            onClick={() => {
                              setDateFrom('');
                              setDateTo('');
                            }}
                          >
                            <X size={16} aria-hidden />
                          </IconButton>
                        )}
                      </div>
                    )}
                  </Field>
                </Toolbar>
                {selectedTicketCfg && (
                  <div className="tq-subfilters">
                    <TicketSubFilters
                      cfg={selectedTicketCfg}
                      filters={subFilters}
                      onChange={setSubFilters}
                      tasks={subFilterBase}
                    />
                  </div>
                )}
              </div>

              {/* A leader looking at the whole team is the one case where the cap
                  really bites: the list holds the newest 200 open and 200 closed
                  across every RM, so an older ticket is absent rather than
                  filtered out, and no chip or date on screen can bring it back.
                  Picking an RM re-applies the cap inside that one queue — their
                  open tickets fit comfortably, so nothing of theirs is hidden. */}
              {!loading && isLeader && !agentFilter && (openTruncated > 0 || closedTruncated > 0) && (
                <Callout tone="warning">
                  Showing the newest{' '}
                  {viewingCompleted
                    ? `${closedTasks.length} of ${closedTruncated} completed`
                    : `${tasks.length} of ${openTruncated} open`}{' '}
                  tickets across the whole team — older ones are not loaded, so filters here cannot
                  reach them. <strong>Pick an RM</strong> to see every one of their open tickets,
                  however old. For totals and trends across everyone, use{' '}
                  <a href={`${process.env.PUBLIC_URL}/pages/rm-team-performance`}>Team Performance</a>.
                </Callout>
              )}

              {loading ? (
                <Loader label="Loading tasks…" />
              ) : filtered.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle size={22} aria-hidden />}
                  title="Nothing here — queue is clear."
                  description="Try another chip, widen the dates or clear a filter."
                />
              ) : (
                <Table caption={title}>
                  <thead>
                    <tr>
                      {/* Reason and Local time are not sortable: one is free
                          text and the other a clock that moves while you read
                          it, so neither gives a stable order. */}
                      <SortableTh sortKey="student" sort={sort} onSort={toggleSort}>
                        Student
                      </SortableTh>
                      <SortableTh sortKey="task" sort={sort} onSort={toggleSort}>
                        Task
                      </SortableTh>
                      {isLeader && !panelBeside && (
                        <SortableTh sortKey="agent" sort={sort} onSort={toggleSort}>
                          Agent
                        </SortableTh>
                      )}
                      <SortableTh
                        sortKey="created"
                        sort={sort}
                        onSort={toggleSort}
                        title="When the ticket was raised (IST)"
                      >
                        Created · IST
                      </SortableTh>
                      <th>Reason / context</th>
                      {!panelBeside && <th title="Customer's current local time">Local time</th>}
                      <SortableTh
                        sortKey="sla"
                        sort={sort}
                        onSort={toggleSort}
                        title="Time to first action — the sheet's SLA. The deadline counts only the RM's working hours, so time outside their shift doesn't eat it. Sorts by the deadline, so the most overdue comes first."
                      >
                        SLA
                      </SortableTh>
                      {factCols.map((c) => (
                        <SortableTh
                          key={c.key}
                          sortKey={`fact:${c.key}`}
                          sort={sort}
                          onSort={toggleSort}
                          title={`From the core system, via the learner's enrolment. Blank where we have no figure yet.`}
                        >
                          {c.label}
                        </SortableTh>
                      ))}
                      <SortableTh sortKey="status" sort={sort} onSort={toggleSort}>
                        Status
                      </SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTasks.map((t) => {
                      const urgency = URGENCY_PILL[urgencyOf(t)];
                      const sla = slaCell(t);
                      const resolution = resolutionNote(t);
                      // Whether THIS ticket is closed, not which chip is
                      // selected. "All" mixes open and closed in one list, so a
                      // closed ticket sitting in it was being drawn as live
                      // work — an urgency pill and a countdown instead of its
                      // own outcome.
                      const isClosed = !!t.closedAt || t.stage === 'CLOSED';
                      const selected = t.id === selectedTaskId;
                      return (
                        <tr
                          key={t.id}
                          className={selected ? 'tq-row tq-row--selected' : 'tq-row'}
                          tabIndex={0}
                          aria-selected={selected}
                          title="Open this ticket"
                          onClick={() => setSelectedTaskId(t.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target === e.currentTarget) setSelectedTaskId(t.id);
                          }}
                        >
                          <td className="tq-col-student">
                            <div className="tq-student">
                              <CountryFlag phone={t.customerPhoneE164} />
                              {t.customerType === 'STUDENT' ? (
                                <button
                                  type="button"
                                  className="tq-student__link"
                                  title="View profile details"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDrawerStudent({
                                      studentExternalId: t.customerExternalId,
                                      name: t.customerName,
                                      phone: t.customerPhoneE164,
                                      email: t.customerEmail,
                                    });
                                  }}
                                >
                                  {t.customerName}
                                </button>
                              ) : (
                                t.customerName
                              )}
                              <MembershipBadge value={t.facts?.membershipType} />
                            </div>
                            {/* Calling from a row shouldn't also open the ticket. */}
                            <span onClick={(e) => e.stopPropagation()}>
                              <ClickToCall
                                className="small"
                                number={t.customerPhoneE164}
                                name={t.customerName}
                                sourceRecordId={t.id}
                                source="admin"
                                fallbackToDialer
                              />
                            </span>
                          </td>
                          <td>
                            <div>{t.taskType.name}</div>
                            <TaskIdChip taskNumber={t.taskNumber} uuid={t.id} />
                          </td>
                          {isLeader && !panelBeside && (
                            <td className="tq-small">
                              {t.assignee ? t.assignee.name : <span className="ds-text-subtle">Unassigned</span>}
                            </td>
                          )}
                          <td className="rm-caption tq-nowrap" title={istFull(t.createdAt)}>
                            {istDateTime(t.createdAt)}
                          </td>
                          <td className="ds-text-muted">{reasonOf(t, ticketCfgByType[t.taskType.key])}</td>
                          {!panelBeside && (
                            <td className="tq-col-local">
                              {(() => {
                                const local = customerLocalTime(t);
                                return local ? <LocalTimeDisplay local={local} /> : <span className="ds-text-subtle">—</span>;
                              })()}
                            </td>
                          )}
                          <td>
                            {isClosed ? (
                              <span className="ds-text-muted">
                                {t.closedAt
                                  ? new Date(t.closedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                                  : '—'}
                              </span>
                            ) : (
                              <>
                                <span
                                  className={`tq-tone--${sla.tone}${sla.bold ? ' tq-bold' : ''}`}
                                  title={
                                    t.firstResponseAt
                                      ? deadlineTitle('First actioned', t.firstResponseAt)
                                      : t.dueAt
                                        ? deadlineTitle('Must be actioned by', t.dueAt)
                                        : undefined
                                  }
                                >
                                  {sla.text}
                                </span>
                                {resolution && (
                                  <div
                                    className={`tq-resolution tq-tone--${resolution.tone}${resolution.bold ? ' tq-bold' : ''}`}
                                    title={
                                      t.resolveDueAt
                                        ? `${deadlineTitle('Must be closed by', t.resolveDueAt)} — counted in the RM's working days, landing at the end of that shift`
                                        : undefined
                                    }
                                  >
                                    {resolution.text}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          {factCols.map((c) => (
                            <td key={c.key} className="tq-nowrap tq-fact">
                              <FactCell value={t.facts?.[c.key]} kind={c.kind} />
                            </td>
                          ))}
                          <td>
                            {isClosed ? (
                              <Badge tone={resolutionTone(t.resolution)}>{t.status.label}</Badge>
                            ) : (
                              <Badge tone={urgency.tone}>{urgency.label}</Badge>
                            )}
                            {!t.assignee && (
                              <div className="rm-caption tq-meta">
                                <em>Unassigned</em>
                              </div>
                            )}
                            {t.followUpAt && (
                              <div className="tq-followup tq-meta">
                                <Clock size={12} aria-hidden />
                                {new Date(t.followUpAt).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}

              {!loading && filtered.length > 0 && (
                <div className="rm-row rm-row--between">
                  <span className="rm-caption">
                    Showing {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filtered.length)} of{' '}
                    {filtered.length}
                    {/* The closed list is capped. Say so, rather than letting a
                        short list read as the complete one — an older ticket
                        that was never fetched cannot be found by any filter. */}
                    {(() => {
                      const capped = viewingCompleted ? closedTruncated : openTruncated;
                      const held = viewingCompleted ? closedTasks.length : tasks.length;
                      if (!capped) return null;
                      return (
                        <span className="tq-tone--warning">
                          {' '}· newest {held} of {capped} loaded — narrow the dates or pick an RM
                          to reach older ones
                        </span>
                      );
                    })()}
                  </span>
                  <div className="rm-row">
                    <span className="rm-caption" aria-hidden="true">Rows per page</span>
                    <Select
                      size="sm"
                      className="tq-page-size"
                      aria-label="Rows per page"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      options={[25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={safePage === 0}
                      onClick={() => setPage(safePage - 1)}
                      aria-label="Previous page"
                      icon={<ChevronLeft size={14} aria-hidden />}
                    />
                    <span className="rm-caption ds-num">
                      {safePage + 1} / {pageCount}
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={safePage >= pageCount - 1}
                      onClick={() => setPage(safePage + 1)}
                      aria-label="Next page"
                      icon={<ChevronRight size={14} aria-hidden />}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {panelBeside && (
          <div ref={panelRef} className="tq-panel">
            {/* Nearly the full viewport: the panel holds the facts, the
                context and the whole timeline, and the timeline is what an
                RM actually came to read. */}
            <Card flush className="tq-panel__card">
              {detailView}
            </Card>
          </div>
        )}
      </div>

      {/* Narrower screens: the same ticket view as a drawer over the list. */}
      <Drawer open={panelOpen && !wide} onClose={() => setSelectedTaskId('')} ariaLabel="Ticket" size="lg">
        {detailView}
      </Drawer>
      <StudentDrawer student={drawerStudent} onClose={() => setDrawerStudent(null)} />
    </Page>
  );
};

export default TaskQueue;
